import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';

const ArtworkDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const { addToCart, isInCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchArtwork();
  }, [id]);

  const fetchArtwork = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/artworks/${id}`);
      setArtwork(response.data);
    } catch (error) {
      console.error('Error fetching artwork:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      showToast('Please login to add items to cart', 'warning');
      navigate('/login');
      return;
    }

    const result = addToCart(artwork);
    if (result.success) {
      showToast('Added to cart!', 'success');
    } else {
      showToast(result.message, 'info');
    }
  };

  const handleContactArtist = () => {
    if (!isAuthenticated) {
      showToast('Please login to message artists', 'warning');
      navigate('/login');
      return;
    }
    navigate(`/messages?recipient=${artwork.artist._id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600"></div>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Artwork not found</h2>
          <Link to="/marketplace" className="btn-primary">
            Back to Marketplace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-8 flex items-center space-x-2 text-sm text-gray-600">
          <Link to="/" className="hover:text-orange-600">Home</Link>
          <span>/</span>
          <Link to="/marketplace" className="hover:text-orange-600">Marketplace</Link>
          <span>/</span>
          <span className="text-gray-900">{artwork.title}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Images */}
          <div>
            {/* Main Image */}
            <div className="bg-white rounded-3xl shadow-african-lg p-4 mb-4">
              <img
                src={artwork.images?.[selectedImage] || 'https://placehold.co/600x600/fb923c/ffffff?text=Artwork'}
                alt={artwork.title}
                className="w-full h-[500px] object-contain rounded-2xl"
              />
            </div>

            {/* Thumbnail Images */}
            {artwork.images && artwork.images.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {artwork.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square rounded-xl overflow-hidden border-4 transition-all duration-200 ${
                      selectedImage === index
                        ? 'border-orange-500 scale-105'
                        : 'border-transparent hover:border-orange-200'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${artwork.title} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Details */}
          <div>
            {/* Title and Price */}
            <div className="mb-6">
              {artwork.featured && (
                <span className="inline-block px-4 py-1 bg-gradient-to-r from-orange-500 to-red-600 text-white text-sm font-bold rounded-full mb-4">
                  ⭐ Featured Artwork
                </span>
              )}
              <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
                {artwork.title}
              </h1>
              <div className="flex items-baseline space-x-4">
                <span className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
                  ${artwork.price?.toLocaleString()}
                </span>
                {!artwork.inStock && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-semibold rounded-full">
                    Sold Out
                  </span>
                )}
              </div>
            </div>

            {/* Artist Info */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 mb-6">
              <Link to={`/artist/${artwork.artist._id}`} className="flex items-center space-x-4 group">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                  {artwork.artist?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Artist</p>
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors duration-200">
                    {artwork.artist?.name}
                  </h3>
                </div>
                <svg className="w-6 h-6 text-orange-600 transform group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h2 className="text-2xl font-display font-bold text-gray-900 mb-3">
                About This Artwork
              </h2>
              <p className="text-gray-700 leading-relaxed">
                {artwork.description}
              </p>
            </div>

            {/* Details */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h3 className="text-xl font-display font-bold text-gray-900 mb-4">
                Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Category</p>
                  <p className="font-semibold text-gray-900 capitalize">
                    {artwork.category?.replace('-', ' ')}
                  </p>
                </div>
                {artwork.medium && (
                  <div>
                    <p className="text-sm text-gray-600">Medium</p>
                    <p className="font-semibold text-gray-900">{artwork.medium}</p>
                  </div>
                )}
                {artwork.dimensions && (
                  <div>
                    <p className="text-sm text-gray-600">Dimensions</p>
                    <p className="font-semibold text-gray-900">
                      {artwork.dimensions.width} × {artwork.dimensions.height}
                      {artwork.dimensions.depth && ` × ${artwork.dimensions.depth}`} {artwork.dimensions.unit}
                    </p>
                  </div>
                )}
                {artwork.year && (
                  <div>
                    <p className="text-sm text-gray-600">Year</p>
                    <p className="font-semibold text-gray-900">{artwork.year}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">Views</p>
                  <p className="font-semibold text-gray-900">{artwork.views || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <p className={`font-semibold ${artwork.inStock ? 'text-green-600' : 'text-red-600'}`}>
                    {artwork.inStock ? 'Available' : 'Sold'}
                  </p>
                </div>
              </div>
            </div>

            {/* Tags */}
            {artwork.tags && artwork.tags.length > 0 && (
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  {artwork.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-orange-50 text-orange-700 rounded-full text-sm font-semibold"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleAddToCart}
                disabled={isInCart(artwork._id) || !artwork.inStock}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                  isInCart(artwork._id) || !artwork.inStock
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : 'btn-primary'
                }`}
              >
                {!artwork.inStock ? 'Sold Out' : isInCart(artwork._id) ? 'Already in Cart' : 'Add to Cart'}
              </button>

              <button
                onClick={handleContactArtist}
                className="w-full py-4 btn-outline"
              >
                Contact Artist
              </button>
            </div>

            {/* Security Info */}
            <div className="mt-6 grid grid-cols-3 gap-4 text-center">
              <div>
                <svg className="w-8 h-8 mx-auto text-green-600 mb-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-gray-600 font-semibold">Secure Payment</p>
              </div>
              <div>
                <svg className="w-8 h-8 mx-auto text-blue-600 mb-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                  <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
                </svg>
                <p className="text-xs text-gray-600 font-semibold">Free Shipping</p>
              </div>
              <div>
                <svg className="w-8 h-8 mx-auto text-orange-600 mb-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-gray-600 font-semibold">Authenticity</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtworkDetail;